import type { Puzzle, GenerateRequest, User, ProgressData, PuzzleSummary } from "@shared/types.ts"

const API_BASE = "/api"

/** Thrown when the server returns 401 — signals the UI to show login screen */
export class AuthError extends Error {
  constructor() {
    super("Authentication required")
    this.name = "AuthError"
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new AuthError()
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Unknown error" }))
    throw new Error((err as { error: string }).error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

// ── Auth API ────────────────────────────────────────────────────

export async function googleLogin(credential: string): Promise<User> {
  const res = await fetch(`${API_BASE}/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Login failed" }))
    throw new Error((err as { error: string }).error ?? `HTTP ${res.status}`)
  }
  const data = (await res.json()) as { user: User }
  return data.user
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, { method: "POST" })
}

export async function getCurrentUser(): Promise<User | null> {
  const res = await fetch(`${API_BASE}/auth/me`)
  if (!res.ok) return null
  const data = (await res.json()) as { user: User | null }
  return data.user
}

// ── Puzzle API ──────────────────────────────────────────────────

export async function generatePuzzle(req: GenerateRequest): Promise<Puzzle> {
  const res = await fetch(`${API_BASE}/puzzles/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  })
  return handleResponse<Puzzle>(res)
}

export async function getPuzzle(id: string): Promise<Puzzle> {
  const res = await fetch(`${API_BASE}/puzzles/${id}`)
  return handleResponse<Puzzle>(res)
}

// ── Puzzle by slug (public — returns puzzle + optional progress) ───

export interface PuzzleWithProgress {
  puzzle: Puzzle
  progress: ProgressData | null
  isFavorited: boolean
}

export async function getPuzzleBySlug(slug: string): Promise<PuzzleWithProgress> {
  const res = await fetch(`${API_BASE}/puzzles/${slug}`)
  return handleResponse<PuzzleWithProgress>(res)
}

// ── Progress API ────────────────────────────────────────────────

export async function saveProgress(
  slug: string,
  progress: Omit<ProgressData, "updatedAt">,
): Promise<void> {
  const res = await fetch(`${API_BASE}/puzzles/${slug}/progress`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(progress),
  })
  await handleResponse(res)
}

// ── Favorites API ───────────────────────────────────────────────

export async function toggleFavorite(slug: string): Promise<{ favorited: boolean }> {
  const res = await fetch(`${API_BASE}/puzzles/${slug}/favorite`, {
    method: "POST",
  })
  return handleResponse<{ favorited: boolean }>(res)
}

export async function getFavorites(): Promise<PuzzleSummary[]> {
  const res = await fetch(`${API_BASE}/puzzles/favorites`)
  const data = await handleResponse<{ puzzles: PuzzleSummary[] }>(res)
  return data.puzzles
}

export async function deletePuzzle(slug: string): Promise<void> {
  const res = await fetch(`${API_BASE}/puzzles/${slug}`, { method: "DELETE" })
  await handleResponse(res)
}

export async function getRecent(): Promise<PuzzleSummary[]> {
  const res = await fetch(`${API_BASE}/puzzles/recent`)
  const data = await handleResponse<{ puzzles: PuzzleSummary[] }>(res)
  return data.puzzles
}
